import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

const SALAS = [
  { id: 'grande', label: 'Sala Grande', short: 'GRANDE', color: 'var(--gold)' },
  { id: 'chica', label: 'Sala Chica', short: 'CHICA', color: '#6db8a0' },
  { id: 'conferencias', label: 'Sala de Conferencias', short: 'CONF.', color: '#8b8fd4' },
]

// Horas cada 30min: 8:00, 8:30, 9:00, 9:30...
const HORAS_SEMANA = Array.from({ length: 24 }, (_, i) => 8 + (i * 0.5))  // 8.0, 8.5, 9.0... hasta 19.5
const HORAS_SABADO = Array.from({ length: 8 }, (_, i) => 9 + (i * 0.5))   // 9.0, 9.5... hasta 12.5

function getSemanaDesde(base) {
  const d = new Date(base)
  d.setHours(0, 0, 0, 0)
  const day = d.getDay() // 0=dom
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  return d
}

function addDays(date, n) {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}

function toISO(date) {
  return date.toISOString().split('T')[0]
}

function formatFecha(date) {
  return date.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' })
}

function formatFechaLarga(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  return date.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
}

function esDomingo(date) { return date.getDay() === 0 }
function esSabado(date) { return date.getDay() === 6 }

// Helper para formatear horas (8.0 -> "8:00", 8.5 -> "8:30")
function formatHora(h) {
  return `${Math.floor(h)}:${h % 1 === 0 ? '00' : '30'}`
}

function getDiasVista(base, mode) {
  if (mode === 'semanal') {
    // Vista semanal completa (lun-sáb)
    const inicio = getSemanaDesde(base)
    return Array.from({ length: 6 }, (_, i) => addDays(inicio, i))
  } else {
    // Vista diaria (hoy + 2 días)
    const hoy = new Date(base)
    hoy.setHours(0, 0, 0, 0)
    return [hoy, addDays(hoy, 1), addDays(hoy, 2)]
  }
}

function horasDisponibles(date) {
  if (esDomingo(date)) return []
  if (esSabado(date)) return HORAS_SABADO
  return HORAS_SEMANA
}

export default function SalasReuniones({ usuario }) {
  const { usuario: authUsuario } = useAuth()
  const isAdmin = usuario?.rol === 'admin'
  const edificioId = isAdmin ? null : (authUsuario?.edificios?.id || null)
  const [tab, setTab] = useState('calendario') // calendario | historial | bloqueados
  const [semanaBase, setSemanaBase] = useState(() => getSemanaDesde(new Date()))
  const [fecha, setFecha] = useState(new Date()) // para vista diaria
  const [diaSeleccionado, setDiaSeleccionado] = useState(() => {
    const h = new Date()
    h.setHours(0, 0, 0, 0)
    return h
  })
  const [reservas, setReservas] = useState([])
  const [bloqueados, setBloqueados] = useState([])
  const [oficinas, setOficinas] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [vistaMode, setVistaMode] = useState(localStorage.getItem('salas_vista_mode') || 'diaria') // 'diaria' o 'semanal'
  const [modalBloq, setModalBloq] = useState(false)
  const [form, setForm] = useState({ oficina_id: '', reservante: '', bloques: 1, observaciones: '' })
  const [formBloq, setFormBloq] = useState({ fecha: '', motivo: '' })
  const [saving, setSaving] = useState(false)
  const [msgOk, setMsgOk] = useState('')
  const [historialFiltro, setHistorialFiltro] = useState({ sala: '', estado: '', oficina: '' })

  // Días visibles del calendario. useMemo evita recrear el array en cada render
  // y previene un loop de carga infinita contra Supabase.
  const diasSemana = useMemo(() => {
    return getDiasVista(vistaMode === 'semanal' ? semanaBase : fecha, vistaMode)
  }, [vistaMode, semanaBase, fecha])

  const toggleVistaMode = () => {
    const newMode = vistaMode === 'diaria' ? 'semanal' : 'diaria'
    setVistaMode(newMode)
    localStorage.setItem('salas_vista_mode', newMode)
  }

  const cargar = useCallback(async () => {
    setLoading(true)
    const inicioSemana = toISO(diasSemana[0])
    const finSemana = toISO(diasSemana[diasSemana.length - 1])

    const [{ data: res }, { data: bloq }] = await Promise.all([
      supabase
        .from('reservas_salas')
        .select('*, oficina:oficinas(numero, titular, email_contacto)')
        .gte('fecha', inicioSemana)
        .lte('fecha', finSemana)
        .order('fecha')
        .order('hora_inicio'),
      supabase
        .from('salas_dias_bloqueados')
        .select('*')
        .order('fecha'),
    ])

    setReservas(res || [])
    setBloqueados(bloq || [])
    setLoading(false)
  }, [diasSemana])

  const cargarHistorial = useCallback(async () => {
    const { data } = await supabase
      .from('reservas_salas')
      .select('*, oficina:oficinas(numero, titular, email_contacto)')
      .order('fecha', { ascending: false })
      .order('hora_inicio')
      .limit(200)

    setReservas(data || [])
  }, [])

  // Cargar oficinas de Rawson siempre (módulo exclusivo Rawson)
  useEffect(() => {
    const fetchOficinas = async () => {
      const RAWSON_ID = '8d39acaa-1abd-4965-9fd4-6dab11a4b2d5'
      const { data } = await supabase
        .from('oficinas')
        .select('id, numero, titular, email_contacto')
        .eq('activa', true)
        .eq('edificio_id', RAWSON_ID)
        .order('numero')

      setOficinas(data || [])
    }

    fetchOficinas()
  }, [])

  useEffect(() => {
    if (tab === 'calendario') {
      cargar()
    } else if (tab === 'historial') {
      cargarHistorial()
    } else if (tab === 'bloqueados') {
      supabase
        .from('salas_dias_bloqueados')
        .select('*')
        .order('fecha')
        .then(({ data }) => setBloqueados(data || []))
    }
  }, [tab, cargar, cargarHistorial])

  // Helpers calendario
  const esBloqueado = (dateStr) => bloqueados.some(b => b.fecha === dateStr)

  const reservasEnBloque = (sala, dateStr, hora) =>
    reservas.filter(r =>
      r.sala === sala &&
      r.fecha === dateStr &&
      r.hora_inicio <= hora &&
      r.hora_fin > hora &&
      r.estado === 'confirmada'
    )

  const bloqueLibre = (sala, dateStr, hora) => reservasEnBloque(sala, dateStr, hora).length === 0

  // Abrir modal nueva reserva
  const hoy = toISO(new Date())

  const abrirModal = (fecha, hora, sala) => {
    const fechaStr = toISO(fecha)
    if (fechaStr < hoy) return // no reservar en el pasado
    if (esBloqueado(fechaStr)) return
    if (!bloqueLibre(sala, fechaStr, hora)) return

    setModal({ tipo: 'nueva', fecha, hora, sala })
    setForm({ oficina_id: '', reservante: '', bloques: 1, observaciones: '' })
  }

  // Guardar reserva
  const guardarReserva = async () => {
    if (!form.oficina_id || !form.reservante.trim()) return

    setSaving(true)

    const fechaStr = toISO(modal.fecha)
    const horaFin = modal.hora + (form.bloques * 0.5)

    // Verificar que todos los bloques estén libres
    const ocupado = reservas.some(r =>
      r.sala === modal.sala &&
      r.fecha === fechaStr &&
      r.estado === 'confirmada' &&
      r.hora_inicio < horaFin &&
      r.hora_fin > modal.hora
    )

    if (ocupado) {
      alert('Alguno de los bloques seleccionados ya está ocupado. Actualizá el calendario.')
      setSaving(false)
      return
    }

    const { data, error } = await supabase
      .from('reservas_salas')
      .insert({
        sala: modal.sala,
        fecha: fechaStr,
        hora_inicio: modal.hora,
        hora_fin: horaFin,
        oficina_id: form.oficina_id,
        reservante: form.reservante.trim(),
        observaciones: form.observaciones.trim() || null,
        creado_por: usuario.id,
      })
      .select('*, oficina:oficinas(numero, titular, email_contacto)')
      .single()

    if (!error && data) {
      await enviarEmailConfirmacion(data)
      setMsgOk('✅ Reserva confirmada y email enviado')
      setTimeout(() => setMsgOk(''), 4000)
      setModal(null)
      cargar()
    }

    setSaving(false)
  }

  // Email confirmación
  const enviarEmailConfirmacion = async (reserva) => {
    const email = reserva.oficina?.email_contacto
    if (!email) return

    const salaLabel = SALAS.find(s => s.id === reserva.sala)?.label || reserva.sala

    await fetch('/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tipo: 'sala',
        destinatario_email: email,
        oficina: reserva.oficina?.numero || '',
        oficina_titular: reserva.oficina?.titular || '',
        edificio: 'Essence Rawson',
        datos: {
          sala_label: salaLabel,
          fecha_larga: formatFechaLarga(reserva.fecha),
          hora_inicio: reserva.hora_inicio,
          hora_fin: reserva.hora_fin,
          reservante: reserva.reservante,
          observaciones: reserva.observaciones || '',
        }
      })
    }).catch(() => {})
  }

  // Cancelar reserva
  const cancelarReserva = async (reserva) => {
    // Validar que no sea pasado
    const ahora = new Date()
    const fechaReserva = new Date(reserva.fecha)
    const horaAhora = ahora.getHours() + (ahora.getMinutes() / 60)
    
    // Si la fecha es anterior a hoy, no permitir cancelar
    if (reserva.fecha < toISO(ahora)) {
      alert('No se pueden cancelar reservas de días anteriores')
      return
    }
    
    // Si es hoy pero la hora ya pasó, no permitir cancelar
    if (reserva.fecha === toISO(ahora) && reserva.hora_fin <= horaAhora) {
      alert('No se pueden cancelar reservas cuyo horario ya transcurrió')
      return
    }
    
    if (!window.confirm('¿Cancelar esta reserva?')) return

    await supabase
      .from('reservas_salas')
      .update({ estado: 'cancelada' })
      .eq('id', reserva.id)

    setModal(null)

    if (tab === 'calendario') cargar()
    else cargarHistorial()
  }

  // Bloquear día
  const bloquearDia = async () => {
    if (!formBloq.fecha) return

    setSaving(true)

    await supabase
      .from('salas_dias_bloqueados')
      .upsert({
        fecha: formBloq.fecha,
        motivo: formBloq.motivo || null,
        creado_por: usuario.id
      })

    setFormBloq({ fecha: '', motivo: '' })
    setModalBloq(false)

    const { data } = await supabase
      .from('salas_dias_bloqueados')
      .select('*')
      .order('fecha')

    setBloqueados(data || [])
    setSaving(false)
  }

  const desbloquearDia = async (id) => {
    if (!window.confirm('¿Desbloquear este día?')) return

    await supabase
      .from('salas_dias_bloqueados')
      .delete()
      .eq('id', id)

    const { data } = await supabase
      .from('salas_dias_bloqueados')
      .select('*')
      .order('fecha')

    setBloqueados(data || [])
  }

  // Calcular bloques máximos disponibles desde hora en sala/fecha
  const bloquesMaximos = (sala, fecha, horaInicio) => {
    if (!modal) return 8 // máximo 4 horas = 8 bloques de 30min

    const fechaStr = toISO(fecha)
    const horasDisp = horasDisponibles(fecha)
    const maxHora = Math.max(...horasDisp) + 1
    let max = 0

    for (let h = horaInicio; h < maxHora && h < horaInicio + 4; h += 0.5) {
      if (!bloqueLibre(sala, fechaStr, h)) break
      max++
    }

    return max
  }

  // Historial filtrado
  const historialFiltrado = reservas.filter(r => {
    if (tab !== 'historial') return true
    if (historialFiltro.sala && r.sala !== historialFiltro.sala) return false
    if (historialFiltro.estado && r.estado !== historialFiltro.estado) return false
    if (historialFiltro.oficina && r.oficina_id !== historialFiltro.oficina) return false
    return true
  })

  const salaInfo = (id) => SALAS.find(s => s.id === id)

  // ─── RENDER ───────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '24px 16px', maxWidth: 1100, margin: '0 auto' }}>
      <style>{`
        .salas-mobile-view { display: none; }
        .salas-desktop-view { display: block; }

        @media (max-width: 768px) {
          .salas-mobile-view { display: block; }
          .salas-desktop-view { display: none; }
        }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 22, color: 'var(--gold)', letterSpacing: 1 }}>Salas de Reuniones</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text3)' }}>Essence Rawson · Gestión de reservas</p>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {['calendario', 'historial', 'bloqueados'].map(t => (
            <button
              key={t}
              className={`btn btn-sm ${tab === t ? 'btn-gold' : 'btn-ghost'}`}
              onClick={() => setTab(t)}
            >
              {t === 'calendario' ? '📅 Calendario' : t === 'historial' ? '📋 Historial' : '🔒 Días bloqueados'}
            </button>
          ))}
        </div>
      </div>

      {msgOk && (
        <div style={{ background: '#1a2e1a', border: '1px solid var(--green)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: 'var(--green)', fontSize: 14 }}>
          {msgOk}
        </div>
      )}

      {/* ─── TAB CALENDARIO ─── */}
      {tab === 'calendario' && (
        <>
          {/* Toggle vista mode + Navegación */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <button className="btn btn-gold btn-sm" onClick={toggleVistaMode}>
              {vistaMode === 'diaria' ? '📅 Vista semanal' : '📆 Vista diaria'}
            </button>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
              {vistaMode === 'semanal' ? (
                <>
                  <button className="btn btn-ghost btn-sm" onClick={() => setSemanaBase(s => addDays(s, -7))}>← Semana ant.</button>
                  <span style={{ fontSize: 14, color: 'var(--text2)', minWidth: 220, textAlign: 'center' }}>
                    {formatFecha(semanaBase)} — {formatFecha(addDays(semanaBase, 5))}
                  </span>
                  <button className="btn btn-ghost btn-sm" onClick={() => setSemanaBase(s => addDays(s, 7))}>Semana sig. →</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setSemanaBase(getSemanaDesde(new Date()))}>Esta semana</button>
                </>
              ) : (
                <>
                  <button className="btn btn-ghost btn-sm" onClick={() => setFecha(f => addDays(f, -1))}>← Anterior</button>
                  <span style={{ fontSize: 14, color: 'var(--text2)', minWidth: 200, textAlign: 'center' }}>
                    {formatFecha(fecha)} → {formatFecha(addDays(fecha, 2))}
                  </span>
                  <button className="btn btn-ghost btn-sm" onClick={() => setFecha(f => addDays(f, 1))}>Siguiente →</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setFecha(new Date())}>Hoy</button>
                </>
              )}
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>Cargando...</div>
          ) : (
            <div>
              {/* Leyenda salas */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                {SALAS.map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text2)' }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color }} />
                    {s.label}
                  </div>
                ))}

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text3)' }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: '#2a2a2a', border: '1px solid #333' }} />
                  Sin disponibilidad
                </div>
              </div>

              {/* VISTA MÓVIL: un día a la vez */}
              <div className="salas-mobile-view">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setDiaSeleccionado(d => addDays(d, -1))}>←</button>

                  <div style={{
                    padding: '6px 16px',
                    borderRadius: 8,
                    textTransform: 'capitalize',
                    fontSize: 14,
                    fontWeight: 600,
                    background: toISO(diaSeleccionado) === hoy ? 'var(--gold)' : 'var(--bg3)',
                    color: toISO(diaSeleccionado) === hoy ? '#000' : 'var(--text1)',
                  }}>
                    {diaSeleccionado.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'short' })}
                    {esBloqueado(toISO(diaSeleccionado)) && ' 🔒'}
                  </div>

                  <button className="btn btn-ghost btn-sm" onClick={() => setDiaSeleccionado(d => addDays(d, 1))}>→</button>
                </div>

                {esDomingo(diaSeleccionado) ? (
                  <div style={{ textAlign: 'center', padding: 32, color: 'var(--text3)', fontSize: 13 }}>Domingos cerrado</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ width: 50, fontSize: 10, color: 'var(--text3)', textAlign: 'right', paddingRight: 8 }}>Hora</th>
                        {SALAS.map(sala => (
                          <th key={sala.id} style={{ fontSize: 10, color: sala.color, textAlign: 'center', fontWeight: 700, padding: '4px 2px' }}>
                            {sala.short}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {horasDisponibles(diaSeleccionado).map(hora => {
                        const fechaStr = toISO(diaSeleccionado)
                        const bloq = esBloqueado(fechaStr)
                        const esPasadoMob = fechaStr < hoy

                        return (
                          <tr key={hora}>
                            <td style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'right', paddingRight: 8, whiteSpace: 'nowrap' }}>
                              {formatHora(hora)}
                            </td>

                            {SALAS.map(sala => {
                              const reservaAqui = reservas.find(r =>
                                r.sala === sala.id &&
                                r.fecha === fechaStr &&
                                r.hora_inicio <= hora &&
                                r.hora_fin > hora &&
                                r.estado === 'confirmada'
                              )

                              const esInicio = reservaAqui && reservaAqui.hora_inicio === hora
                              const duracion = reservaAqui ? reservaAqui.hora_fin - reservaAqui.hora_inicio : 1

                              if (reservaAqui && !esInicio) return null

                              const libre = !bloq && !reservaAqui && !esPasadoMob

                              return (
                                <td
                                  key={sala.id}
                                  rowSpan={reservaAqui ? duracion : 1}
                                  onClick={() => libre && abrirModal(diaSeleccionado, hora, sala.id)}
                                  style={{ padding: 2, cursor: libre ? 'pointer' : 'default', verticalAlign: 'top' }}
                                >
                                  <div
                                    style={{
                                      height: reservaAqui ? `${duracion * 36 - 4}px` : '32px',
                                      minHeight: 32,
                                      borderRadius: 6,
                                      fontSize: 10,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      padding: '2px 4px',
                                      textAlign: 'center',
                                      overflow: 'hidden',
                                      background: bloq || esPasadoMob ? '#1a1a1a' : reservaAqui ? `${sala.color}22` : 'transparent',
                                      border: bloq || esPasadoMob ? '1px solid #1f1f1f' : reservaAqui ? `1px solid ${sala.color}66` : libre ? '1px dashed #333' : '1px solid transparent',
                                      color: reservaAqui ? sala.color : 'var(--text3)',
                                    }}
                                    onClick={reservaAqui ? (e) => {
                                      e.stopPropagation()
                                      setModal({ tipo: 'detalle', reserva: reservaAqui, sala })
                                    } : undefined}
                                  >
                                    {reservaAqui ? (
                                      <div style={{ fontWeight: 600, fontSize: 10, lineHeight: 1.2 }}>
                                        {reservaAqui.oficina ? `${reservaAqui.oficina.numero}` : ''}
                                        {duracion > 1 && (
                                          <div style={{ fontSize: 9, color: 'var(--text3)' }}>
                                            {formatHora(reservaAqui.hora_inicio)}–{formatHora(reservaAqui.hora_fin)}h
                                          </div>
                                        )}
                                      </div>
                                    ) : !bloq && !esPasadoMob ? (
                                      <span style={{ fontSize: 10, color: '#444' }}>+</span>
                                    ) : null}
                                  </div>
                                </td>
                              )
                            })}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* VISTA DESKTOP: tabla semanal completa */}
              <div className="salas-desktop-view">
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 60, padding: '8px 4px', fontSize: 11, color: 'var(--text3)', textAlign: 'right', paddingRight: 12 }}>Hora</th>

                      {diasSemana.map((dia, i) => {
                        const esHoy = toISO(dia) === toISO(new Date())
                        const bloq = esBloqueado(toISO(dia))
                        const dom = esDomingo(dia)

                        return (
                          <th key={i} style={{ padding: '8px 4px', textAlign: 'center', fontSize: 12 }} colSpan={3}>
                            <div style={{
                              display: 'inline-block',
                              padding: '4px 10px',
                              borderRadius: 6,
                              background: esHoy ? 'var(--gold)' : 'transparent',
                              color: esHoy ? '#000' : (dom || bloq) ? 'var(--text3)' : 'var(--text2)',
                              fontWeight: esHoy ? 700 : 400,
                              textTransform: 'capitalize'
                            }}>
                              {formatFecha(dia)}
                              {bloq && <span style={{ marginLeft: 4, fontSize: 10 }}>🔒</span>}
                            </div>
                          </th>
                        )
                      })}
                    </tr>

                    <tr>
                      <th />
                      {diasSemana.map((dia, di) =>
                        SALAS.map((sala, salaIdx) => (
                          <th
                            key={`${di}-${sala.id}`}
                            style={{
                              padding: '4px 2px',
                              fontSize: 9,
                              color: sala.color,
                              textAlign: 'center',
                              fontWeight: 700,
                              opacity: esDomingo(dia) ? 0.3 : 1,
                              letterSpacing: 0.5,
                              borderRight: salaIdx === SALAS.length - 1 ? '1px solid #2a2a2a' : 'none'
                            }}
                          >
                            {sala.short}
                          </th>
                        ))
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {HORAS_SEMANA.map(hora => (
                      <tr key={hora}>
                        <td style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'right', paddingRight: 12, paddingTop: 2, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {formatHora(hora)}
                        </td>

                        {diasSemana.map((dia, di) => {
                          const fechaStr = toISO(dia)
                          const horasDisp = horasDisponibles(dia)
                          const disponible = horasDisp.includes(hora)
                          const bloq = esBloqueado(fechaStr)

                          return SALAS.map((sala, salaIdx) => {
                            const reservaAqui = reservas.find(r =>
                              r.sala === sala.id &&
                              r.fecha === fechaStr &&
                              r.hora_inicio <= hora &&
                              r.hora_fin > hora &&
                              r.estado === 'confirmada'
                            )

                            const esInicio = reservaAqui && reservaAqui.hora_inicio === hora
                            const duracion = reservaAqui ? reservaAqui.hora_fin - reservaAqui.hora_inicio : 0.5
                            const numFilas = duracion / 0.5 // cada fila = 30min
                            const esUltimaSala = salaIdx === SALAS.length - 1
                            const esPrimeraSala = salaIdx === 0
                            const diaParImpar = di % 2 === 0

                            if (reservaAqui && !esInicio) return null // merged cell

                            const esPasado = toISO(dia) < hoy
                            const libre = disponible && !bloq && !reservaAqui && !esPasado

                            return (
                              <td
                                key={`${di}-${sala.id}-${hora}`}
                                rowSpan={reservaAqui ? numFilas : 1}
                                onClick={() => libre && abrirModal(dia, hora, sala.id)}
                                style={{
                                  padding: 2,
                                  cursor: libre ? 'pointer' : 'default',
                                  verticalAlign: 'top',
                                  borderRight: esUltimaSala ? '1px solid #2a2a2a' : 'none',
                                  borderLeft: esPrimeraSala ? '4px solid #444' : 'none',
                                  background: diaParImpar ? '#0d0d0d' : 'transparent',
                                }}
                              >
                                <div
                                  style={{
                                    height: reservaAqui ? `${numFilas * 32 - 4}px` : '28px',
                                    minHeight: 28,
                                    borderRadius: 5,
                                    fontSize: 11,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    padding: '2px 4px',
                                    textAlign: 'center',
                                    transition: 'all 0.15s',
                                    overflow: 'hidden',
                                    background: !disponible || bloq
                                      ? '#1a1a1a'
                                      : reservaAqui
                                        ? `${sala.color}22`
                                        : 'transparent',
                                    border: !disponible || bloq
                                      ? '1px solid #1f1f1f'
                                      : reservaAqui
                                        ? `1px solid ${sala.color}66`
                                        : libre
                                          ? '1px dashed #333'
                                          : '1px solid transparent',
                                    color: reservaAqui ? sala.color : 'var(--text3)',
                                  }}
                                  onMouseEnter={e => libre && (e.currentTarget.style.background = `${sala.color}18`)}
                                  onMouseLeave={e => libre && (e.currentTarget.style.background = 'transparent')}
                                  onClick={reservaAqui ? (e) => {
                                    e.stopPropagation()
                                    setModal({ tipo: 'detalle', reserva: reservaAqui, sala })
                                  } : undefined}
                                >
                                  {reservaAqui ? (
                                    <>
                                      <div style={{ fontWeight: 600, fontSize: 11, lineHeight: 1.2 }}>
                                        {reservaAqui.oficina ? `${reservaAqui.oficina.numero} — ${reservaAqui.oficina.titular}` : ''}
                                      </div>
                                      {numFilas > 1 && (
                                        <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                                          {reservaAqui.reservante}
                                        </div>
                                      )}
                                      {numFilas > 2 && (
                                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                                          {formatHora(reservaAqui.hora_inicio)} – {formatHora(reservaAqui.hora_fin)}
                                        </div>
                                      )}
                                    </>
                                  ) : !disponible || bloq ? (
                                    <span style={{ fontSize: 9, color: '#333' }}>—</span>
                                  ) : (
                                    <span style={{ fontSize: 9, color: '#444' }}>+</span>
                                  )}
                                </div>
                              </td>
                            )
                          })
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ─── TAB HISTORIAL ─── */}
      {tab === 'historial' && (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
            <select
              className="input input-sm"
              value={historialFiltro.sala}
              onChange={e => setHistorialFiltro(f => ({ ...f, sala: e.target.value }))}
              style={{ minWidth: 160 }}
            >
              <option value="">Todas las salas</option>
              {SALAS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>

            <select
              className="input input-sm"
              value={historialFiltro.estado}
              onChange={e => setHistorialFiltro(f => ({ ...f, estado: e.target.value }))}
              style={{ minWidth: 150 }}
            >
              <option value="">Todos los estados</option>
              <option value="confirmada">Confirmada</option>
              <option value="cancelada">Cancelada</option>
            </select>

            <select
              className="input input-sm"
              value={historialFiltro.oficina}
              onChange={e => setHistorialFiltro(f => ({ ...f, oficina: e.target.value }))}
              style={{ minWidth: 180 }}
            >
              <option value="">Todas las oficinas</option>
              {oficinas.map(o => <option key={o.id} value={o.id}>{o.numero} — {o.titular}</option>)}
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {historialFiltrado.length === 0 && (
              <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>Sin reservas</div>
            )}

            {historialFiltrado.map(r => {
              const s = salaInfo(r.sala)

              return (
                <div
                  key={r.id}
                  style={{
                    background: 'var(--card)',
                    borderRadius: 10,
                    padding: '14px 18px',
                    border: `1px solid ${r.estado === 'cancelada' ? '#2a2a2a' : s?.color + '33' || '#333'}`,
                    opacity: r.estado === 'cancelada' ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    flexWrap: 'wrap'
                  }}
                >
                  <div style={{ fontSize: 20 }}>{s?.icon}</div>

                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontWeight: 600, color: s?.color, fontSize: 13 }}>{s?.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                      {formatFechaLarga(r.fecha)} · {formatHora(r.hora_inicio)} – {formatHora(r.hora_fin)} hs
                    </div>
                  </div>

                  <div style={{ minWidth: 140 }}>
                    <div style={{ fontSize: 13, color: 'var(--text1)', fontWeight: 500 }}>
                      {r.oficina ? `${r.oficina.numero} — ${r.oficina.titular}` : ''}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>{r.reservante}</div>
                  </div>

                  <div>
                    <span style={{
                      fontSize: 11,
                      padding: '3px 10px',
                      borderRadius: 20,
                      fontWeight: 600,
                      background: r.estado === 'confirmada' ? '#1a2e1a' : '#2a1a1a',
                      color: r.estado === 'confirmada' ? 'var(--green)' : '#e05555',
                      border: `1px solid ${r.estado === 'confirmada' ? 'var(--green)' : '#e05555'}44`
                    }}>
                      {r.estado === 'confirmada' ? '✓ Confirmada' : '✗ Cancelada'}
                    </span>
                  </div>

                  {r.estado === 'confirmada' && (() => {
                    const ahora = new Date()
                    const horaAhora = ahora.getHours() + (ahora.getMinutes() / 60)
                    const esPasado = r.fecha < toISO(ahora) || (r.fecha === toISO(ahora) && r.hora_fin <= horaAhora)
                    
                    if (esPasado) return null
                    
                    return (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: '#e05555', fontSize: 12 }}
                        onClick={() => cancelarReserva(r)}
                      >
                        Cancelar
                      </button>
                    )
                  })()}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ─── TAB BLOQUEADOS ─── */}
      {tab === 'bloqueados' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text3)' }}>
              Bloqueá feriados u otros días donde las salas no estarán disponibles.<br />
              Los domingos se bloquean automáticamente, no es necesario cargarlos acá.
            </p>

            <button className="btn btn-gold btn-sm" onClick={() => setModalBloq(true)}>+ Bloquear día</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {bloqueados.length === 0 && (
              <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>Sin días bloqueados</div>
            )}

            {bloqueados.map(b => (
              <div
                key={b.id}
                style={{
                  background: 'var(--card)',
                  borderRadius: 10,
                  padding: '14px 18px',
                  border: '1px solid #333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16
                }}
              >
                <span style={{ fontSize: 20 }}>🔒</span>

                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: 'var(--text1)', fontSize: 14, textTransform: 'capitalize' }}>
                    {formatFechaLarga(b.fecha)}
                  </div>
                  {b.motivo && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{b.motivo}</div>}
                </div>

                <button
                  className="btn btn-ghost btn-sm"
                  style={{ color: '#e05555', fontSize: 12 }}
                  onClick={() => desbloquearDia(b.id)}
                >
                  Desbloquear
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── MODAL NUEVA RESERVA ─── */}
      {modal?.tipo === 'nueva' && (
        <div
          style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setModal(null)}
        >
          <div
            style={{ background: 'var(--bg2)', borderRadius: 14, padding: 28, width: '100%', maxWidth: 420, border: '1px solid var(--border)' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 6px', color: 'var(--gold)', fontSize: 16 }}>Nueva Reserva</h3>

            <p style={{ margin: '0 0 20px', fontSize: 12, color: 'var(--text3)' }}>
              {SALAS.find(s => s.id === modal.sala)?.icon} {SALAS.find(s => s.id === modal.sala)?.label} · {formatFechaLarga(toISO(modal.fecha))} desde {formatHora(modal.hora)} hs
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Oficina *</label>
                <select
                  className="input"
                  value={form.oficina_id}
                  onChange={e => setForm(f => ({ ...f, oficina_id: e.target.value }))}
                >
                  <option value="">Seleccioná una oficina</option>
                  {oficinas.map(o => <option key={o.id} value={o.id}>{o.numero} — {o.titular}</option>)}
                </select>
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Nombre del reservante *</label>
                <input
                  className="input"
                  placeholder="Nombre y apellido"
                  value={form.reservante}
                  onChange={e => setForm(f => ({ ...f, reservante: e.target.value }))}
                />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>
                  Cantidad de bloques (30min c/u) — máx. {bloquesMaximos(modal.sala, modal.fecha, modal.hora)}
                </label>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Array.from({ length: bloquesMaximos(modal.sala, modal.fecha, modal.hora) }, (_, i) => i + 1).map(n => (
                    <button
                      key={n}
                      className={`btn btn-sm ${form.bloques === n ? 'btn-gold' : 'btn-ghost'}`}
                      onClick={() => setForm(f => ({ ...f, bloques: n }))}
                    >
                      {n === 1 ? '30m' : n === 2 ? '1h' : n === 3 ? '1h30' : n === 4 ? '2h' : n === 5 ? '2h30' : n === 6 ? '3h' : n === 7 ? '3h30' : '4h'}
                    </button>
                  ))}
                </div>

                <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--text3)' }}>
                  {formatHora(modal.hora)} → {formatHora(modal.hora + (form.bloques * 0.5))} hs
                </p>
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Observaciones</label>
                <textarea
                  className="input"
                  placeholder="Opcional..."
                  rows={2}
                  value={form.observaciones}
                  onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))}
                  style={{ resize: 'vertical' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancelar</button>

              <button
                className="btn btn-gold"
                onClick={guardarReserva}
                disabled={saving || !form.oficina_id || !form.reservante.trim()}
              >
                {saving ? 'Guardando...' : '✓ Confirmar reserva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL DETALLE RESERVA ─── */}
      {modal?.tipo === 'detalle' && (
        <div
          style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setModal(null)}
        >
          <div
            style={{ background: 'var(--bg2)', borderRadius: 14, padding: 28, width: '100%', maxWidth: 380, border: `1px solid ${modal.sala?.color}44` }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 20px', color: modal.sala?.color, fontSize: 16 }}>
              {modal.sala?.icon} {modal.sala?.label}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
              {[
                ['Fecha', formatFechaLarga(modal.reserva.fecha)],
                ['Horario', `${formatHora(modal.reserva.hora_inicio)} – ${formatHora(modal.reserva.hora_fin)} hs`],
                ['Oficina', modal.reserva.oficina ? `${modal.reserva.oficina.numero} — ${modal.reserva.oficina.titular}` : ''],
                ['Reservante', modal.reserva.reservante],
                modal.reserva.observaciones ? ['Observaciones', modal.reserva.observaciones] : null,
              ].filter(Boolean).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', gap: 12 }}>
                  <span style={{ color: 'var(--text3)', minWidth: 90 }}>{k}</span>
                  <span style={{ color: 'var(--text1)', textTransform: k === 'Fecha' ? 'capitalize' : 'none' }}>{v}</span>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cerrar</button>

              {(() => {
                const ahora = new Date()
                const horaAhora = ahora.getHours() + (ahora.getMinutes() / 60)
                const esPasado = modal.reserva.fecha < toISO(ahora) || (modal.reserva.fecha === toISO(ahora) && modal.reserva.hora_fin <= horaAhora)
                
                if (esPasado) return null
                
                return (
                  <button className="btn btn-ghost" style={{ color: '#e05555' }} onClick={() => cancelarReserva(modal.reserva)}>
                    Cancelar reserva
                  </button>
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL BLOQUEAR DÍA ─── */}
      {modalBloq && (
        <div
          style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setModalBloq(false)}
        >
          <div
            style={{ background: 'var(--bg2)', borderRadius: 14, padding: 28, width: '100%', maxWidth: 360, border: '1px solid var(--border)' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 20px', color: 'var(--gold)', fontSize: 16 }}>🔒 Bloquear día</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Fecha *</label>
                <input
                  className="input"
                  type="date"
                  value={formBloq.fecha}
                  onChange={e => setFormBloq(f => ({ ...f, fecha: e.target.value }))}
                />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Motivo (opcional)</label>
                <input
                  className="input"
                  placeholder="Ej: Feriado nacional"
                  value={formBloq.motivo}
                  onChange={e => setFormBloq(f => ({ ...f, motivo: e.target.value }))}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setModalBloq(false)}>Cancelar</button>

              <button className="btn btn-gold" onClick={bloquearDia} disabled={saving || !formBloq.fecha}>
                {saving ? 'Bloqueando...' : 'Bloquear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}